import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, MessageSquare, HelpCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const isFormValid = name.trim() && email.trim() && message.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { name, email, message },
      });

      if (error) throw error;

      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours.",
      });
      
      setName("");
      setEmail("");
      setMessage("");
    } catch (error: any) {
      toast({
        title: "Failed to send message",
        description: "Please try again or email us directly at support@airportbuddy.app",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Support - Get Help in 24 Hours | Airport Buddy</title>
        <meta name="description" content="💬 Need help? Our support team responds within 24 hours. Get answers about TSA wait times, flight tracking, and airport info." />
        <link rel="canonical" href="https://airportbuddy.app/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Airport Buddy Support" />
        <meta property="og:description" content="Reach the Airport Buddy team — we respond within 24 hours." />
        <meta property="og:url" content="https://airportbuddy.app/contact" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6 min-h-[44px]">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Home
            </Link>
          </Button>

          <div className="max-w-3xl mx-auto space-y-8">
            <header className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                Contact Us
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground">
                We're here to help with any questions or feedback
              </p>
            </header>

            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto p-3 bg-primary/10 rounded-xl w-fit mb-2">
                    <Mail className="h-8 w-8 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-lg">Email Us</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-base text-muted-foreground mb-2">
                    General inquiries
                  </p>
                  <a 
                    href="mailto:support@airportbuddy.app" 
                    className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                  >
                    support@airportbuddy.app
                  </a>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto p-3 bg-primary/10 rounded-xl w-fit mb-2">
                    <MessageSquare className="h-8 w-8 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-lg">Live Chat</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-base text-muted-foreground mb-2">
                    Available 24/7
                  </p>
                  <p className="text-primary">Coming Soon</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto p-3 bg-primary/10 rounded-xl w-fit mb-2">
                    <HelpCircle className="h-8 w-8 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-lg">Help Center</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-base text-muted-foreground mb-2">
                    Find quick answers
                  </p>
                  <Link 
                    to="/#faq" 
                    className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                  >
                    Visit FAQ
                  </Link>
                </CardContent>
              </Card>
            </div>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-base">Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      required
                      disabled={isSubmitting}
                      aria-required="true"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      disabled={isSubmitting}
                      aria-required="true"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-base">Message</Label>
                    <Textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="How can we help you?"
                      rows={6}
                      required
                      disabled={isSubmitting}
                      aria-required="true"
                      className="min-h-[120px] text-base resize-y"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full min-h-[48px]"
                    disabled={isSubmitting || !isFormValid}
                  >
                    {isSubmitting ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h2 className="text-xl md:text-2xl font-semibold mb-4">Frequently Asked Questions</h2>
                <div className="space-y-5 text-base">
                  <div>
                    <p className="font-semibold text-foreground mb-1">How accurate are the wait times?</p>
                    <p className="text-muted-foreground">
                      Our wait times are updated in real-time and sourced from official TSA data and 
                      user reports, ensuring high accuracy.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Which airports do you cover?</p>
                    <p className="text-muted-foreground">
                      We cover all major US airports. Check our homepage for the complete list.
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Is Airport Wait Buddy free?</p>
                    <p className="text-muted-foreground">
                      Yes! Airport Wait Buddy is completely free to use with no hidden fees.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
